'use client';
import React, { useState } from 'react';
import { Building2, Globe, ExternalLink, ChevronDown } from 'lucide-react';

interface Institution {
  name: string;
  description: string;
  url: string;
}

interface CountryData {
  flag: string;
  institution: Institution;
}

const COUNTRY_DATA: Record<string, CountryData> = {
  'South Africa': {
    flag: '🇿🇦',
    institution: {
      name: 'Companies and Intellectual Property Commission (CIPC)',
      description: 'Official government body for company registration in South Africa.',
      url: 'https://www.cipc.co.za/index.php/register-your-business/',
    },
  },
  'United States': {
    flag: '🇺🇸',
    institution: {
      name: 'National Association of Secretaries of State (NASS)',
      description: 'Portal to register your business entity in any US state.',
      url: 'https://www.nass.org/business-services/start-a-business',
    },
  },
  'United Kingdom': {
    flag: '🇬🇧',
    institution: {
      name: 'Companies House',
      description: 'UK government registrar of companies — register online in minutes.',
      url: 'https://www.gov.uk/limited-company-formation/register-your-company',
    },
  },
  'Canada': {
    flag: '🇨🇦',
    institution: {
      name: 'Corporations Canada',
      description: 'Federal body for incorporating a business in Canada.',
      url: 'https://www.ic.gc.ca/eic/site/cd-dgc.nsf/eng/cs07316.html',
    },
  },
  'Australia': {
    flag: '🇦🇺',
    institution: {
      name: 'Australian Securities and Investments Commission (ASIC)',
      description: 'Register a company or business name with ASIC.',
      url: 'https://asic.gov.au/for-business/registering-a-company/',
    },
  },
  'Nigeria': {
    flag: '🇳🇬',
    institution: {
      name: 'Corporate Affairs Commission (CAC)',
      description: 'Nigeria\'s official body for business name and company registration.',
      url: 'https://www.cac.gov.ng/register-a-business/',
    },
  },
  'Kenya': {
    flag: '🇰🇪',
    institution: {
      name: 'Business Registration Service (BRS) Kenya',
      description: 'Online portal for registering companies and business names in Kenya.',
      url: 'https://www.businessregistration.go.ke/',
    },
  },
  'Ghana': {
    flag: '🇬🇭',
    institution: {
      name: 'Registrar General\'s Department (RGD)',
      description: 'Ghana\'s official company and business registration authority.',
      url: 'https://rgd.gov.gh/register-a-business/',
    },
  },
  'India': {
    flag: '🇮🇳',
    institution: {
      name: 'Ministry of Corporate Affairs (MCA21)',
      description: 'India\'s online portal for company incorporation and registration.',
      url: 'https://www.mca.gov.in/content/mca/global/en/mca/fo-llp-filing/company-registration.html',
    },
  },
  'Germany': {
    flag: '🇩🇪',
    institution: {
      name: 'Handelsregister (Commercial Register)',
      description: 'Germany\'s official commercial register for business registration.',
      url: 'https://www.handelsregister.de/',
    },
  },
  'France': {
    flag: '🇫🇷',
    institution: {
      name: 'Guichet des Formalités des Entreprises',
      description: 'France\'s single online portal for all business registration formalities.',
      url: 'https://formalites.entreprises.gouv.fr/',
    },
  },
  'Brazil': {
    flag: '🇧🇷',
    institution: {
      name: 'Junta Comercial (REDESIM)',
      description: 'Brazil\'s integrated system for business registration and licensing.',
      url: 'https://redesim.gov.br/',
    },
  },
  'Singapore': {
    flag: '🇸🇬',
    institution: {
      name: 'Accounting and Corporate Regulatory Authority (ACRA)',
      description: 'Singapore\'s national regulator for business entities and public accountants.',
      url: 'https://www.acra.gov.sg/registration/register-a-business-entity/',
    },
  },
  'United Arab Emirates': {
    flag: '🇦🇪',
    institution: {
      name: 'Department of Economic Development (DED)',
      description: 'UAE\'s authority for trade licensing and company registration.',
      url: 'https://www.dubaided.gov.ae/en/Pages/StartaBusiness.aspx',
    },
  },
  'Zimbabwe': {
    flag: '🇿🇼',
    institution: {
      name: 'Zimbabwe Investment and Development Agency (ZIDA)',
      description: 'One-stop shop for business registration and investment in Zimbabwe.',
      url: 'https://www.zida.gov.zw/register-a-business/',
    },
  },
  'Zambia': {
    flag: '🇿🇲',
    institution: {
      name: 'Patents and Companies Registration Agency (PACRA)',
      description: 'Zambia\'s official body for company and business name registration.',
      url: 'https://www.pacra.org.zm/',
    },
  },
  'Tanzania': {
    flag: '🇹🇿',
    institution: {
      name: 'Business Registrations and Licensing Agency (BRELA)',
      description: 'Tanzania\'s agency for business name and company registration.',
      url: 'https://www.brela.go.tz/',
    },
  },
  'Rwanda': {
    flag: '🇷🇼',
    institution: {
      name: 'Rwanda Development Board (RDB)',
      description: 'Rwanda\'s one-stop centre for business registration.',
      url: 'https://www.rdb.rw/doing-business/company-registration/',
    },
  },
  'Ethiopia': {
    flag: '🇪🇹',
    institution: {
      name: 'Ethiopian Investment Commission (EIC)',
      description: 'Ethiopia\'s authority for business registration and investment licensing.',
      url: 'https://www.investethiopia.gov.et/index.php/investment-guide/business-registration',
    },
  },
  'Botswana': {
    flag: '🇧🇼',
    institution: {
      name: 'Companies and Intellectual Property Authority (CIPA)',
      description: 'Botswana\'s official company and IP registration authority.',
      url: 'https://www.cipa.co.bw/register-a-company/',
    },
  },
  'Namibia': {
    flag: '🇳🇦',
    institution: {
      name: 'Business and Intellectual Property Authority (BIPA)',
      description: 'Namibia\'s authority for business and intellectual property registration.',
      url: 'https://www.bipa.na/services/business-registration/',
    },
  },
  'Mozambique': {
    flag: '🇲🇿',
    institution: {
      name: 'Balcão de Atendimento Único (BAU)',
      description: 'Mozambique\'s one-stop shop for business registration.',
      url: 'https://www.bau.gov.mz/',
    },
  },
  'Netherlands': {
    flag: '🇳🇱',
    institution: {
      name: 'Netherlands Chamber of Commerce (KVK)',
      description: 'Register your business in the Dutch Business Register.',
      url: 'https://www.kvk.nl/en/registration/register-your-business/',
    },
  },
  'Sweden': {
    flag: '🇸🇪',
    institution: {
      name: 'Swedish Companies Registration Office (Bolagsverket)',
      description: 'Sweden\'s official registrar for companies and associations.',
      url: 'https://bolagsverket.se/en/start-and-register-a-company',
    },
  },
  'New Zealand': {
    flag: '🇳🇿',
    institution: {
      name: 'Companies Office New Zealand',
      description: 'New Zealand\'s online company registration service.',
      url: 'https://www.companiesoffice.govt.nz/companies/register-a-company/',
    },
  },
};

const COUNTRIES = Object.keys(COUNTRY_DATA).sort();

export default function CompanyRegistrationSection() {
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  const institution = selectedCountry ? COUNTRY_DATA[selectedCountry]?.institution : null;
  const flag = selectedCountry ? COUNTRY_DATA[selectedCountry]?.flag : null;

  return (
    <section style={{ background: '#0f0c00', borderTop: '1px solid #2e2000' }}>
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-14">
        {/* Heading */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4" style={{ background: 'rgba(196,154,0,0.12)', border: '1px solid rgba(196,154,0,0.25)' }}>
            <Building2 size={22} style={{ color: '#C49A00' }} />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: '#f5edd0' }}>
            Register Your Intellectual Business
          </h2>
          <p className="text-sm max-w-xl mx-auto leading-relaxed" style={{ color: '#a09050' }}>
            Ready to formalise your intellectual business or company? Select your country below to discover the official registration institution and get started today.
          </p>
        </div>

        {/* Country Selector */}
        <div className="max-w-md mx-auto mb-8">
          <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#a09050' }}>
            <Globe size={12} className="inline mr-1" />
            Select Your Country
          </label>
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all"
              style={{
                background: '#1a1200',
                border: '1px solid #3a2e00',
                color: selectedCountry ? '#f5edd0' : '#7a6030',
              }}
            >
              <span className="flex items-center gap-2">
                {flag && <span className="text-lg">{flag}</span>}
                {selectedCountry || 'Choose a country…'}
              </span>
              <ChevronDown size={16} style={{ color: '#a09050', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>

            {isOpen && (
              <div
                className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-2xl"
                style={{ background: '#1a1200', border: '1px solid #3a2e00', maxHeight: '260px', overflowY: 'auto' }}
              >
                {COUNTRIES.map(country => (
                  <button
                    key={country}
                    onClick={() => { setSelectedCountry(country); setIsOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors"
                    style={{
                      color: selectedCountry === country ? '#C49A00' : '#c8b870',
                      background: selectedCountry === country ? 'rgba(196,154,0,0.1)' : 'transparent',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(196,154,0,0.08)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = selectedCountry === country ? 'rgba(196,154,0,0.1)' : 'transparent'; }}
                  >
                    <span className="text-base">{COUNTRY_DATA[country].flag}</span>
                    {country}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Institution Card */}
        {institution && (
          <div
            className="max-w-lg mx-auto rounded-2xl p-6 transition-all"
            style={{ background: '#1a1200', border: '1px solid rgba(196,154,0,0.3)' }}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="text-3xl flex-shrink-0">{flag}</div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#a09050' }}>{selectedCountry} — Official Registration Body</p>
                <h3 className="text-base font-bold leading-snug" style={{ color: '#f5edd0' }}>{institution.name}</h3>
              </div>
            </div>
            <p className="text-sm mb-5 leading-relaxed" style={{ color: '#a09050' }}>{institution.description}</p>
            <a
              href={institution.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{ background: 'linear-gradient(135deg,#C49A00,#9A7800)', color: '#ffffff' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.88'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
            >
              Register Now
              <ExternalLink size={14} />
            </a>
          </div>
        )}

        {!selectedCountry && (
          <p className="text-center text-xs" style={{ color: '#4a3e10' }}>
            Select a country above to see its registration institution
          </p>
        )}
      </div>
    </section>
  );
}
